import { Grid, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useMemo, useContext, useEffect } from 'react';
import { CONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import HistoryIcon from '@mui/icons-material/History';
import CanRiskModal from './export-menu/CanRiskModal';
import { downloadCanRisk } from './export-menu/canriskDownload';
import RiskRecordsList from './RiskRecordsList';
import GailScoreModal from './GailScoreModal';
import { SIDEBAR_VALUE } from '../sidebarValues';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const ButtonGroup = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 15
});

function RiskFactorsMenu({ data, modificationHistoryHandle, reportSummary }) {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);
  const [gailModalOpen, setGailModalOpen] = useState(false);
  const [canRiskModalOpen, setCanRiskModalOpen] = useState(false);

  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const familyId = user?.familyId || user_id;
  const gender = useMemo(() => data?.gender, [data]);
  // Canonical member key — scopes the score cards to the selected member.
  const memberId = useMemo(
    () => data?.proBandId ?? data?.familyMemberId,
    [data?.proBandId, data?.familyMemberId]
  );

  // TGCLOUD-1669 Phase 1 — the collapsed-row summary.
  //
  // This component owns no records: two or three RiskRecordsList children each
  // fetch and filter their own. So the facts are collected from them and merged
  // here rather than refetched. The GAIL list only mounts for an assigned-female
  // record, so the set of reporters varies and we aggregate whoever mounted.
  const [riskFacts, setRiskFacts] = useState({});

  // FORGET THE PREVIOUS PATIENT. `riskFacts` only ever merged, and the Gail
  // list is mounted for a female only, so selecting a woman with N Gail scores
  // and then a man left `gail: { count: N }` in place with nothing to withdraw
  // it: the summary line reported one patient's score count under another
  // patient's name. That is the exact failure the three-valued summary contract
  // exists to prevent, so the facts are cleared the moment the member changes
  // and each list reports again from pending.
  useEffect(() => {
    setRiskFacts({});
  }, [memberId, gender]);
  const handleRiskFacts = React.useCallback((recordType, facts) => {
    setRiskFacts((prev) => {
      const before = prev[recordType];
      if (
        before &&
        before.status === facts.status &&
        before.count === facts.count
      ) {
        return prev;
      }
      return { ...prev, [recordType]: facts };
    });
  }, []);

  useEffect(() => {
    if (!reportSummary) return;
    const entries = Object.values(riskFacts);
    // Nothing has reported yet, or one list is still loading: say nothing
    // rather than reporting a total that is about to change.
    if (!entries.length || entries.some((e) => e.status === 'pending')) {
      reportSummary(SIDEBAR_VALUE.riskFactors, { status: 'pending' });
      return;
    }
    const count = entries.reduce((n, e) => n + e.count, 0);
    const leads = entries
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .map((e) => `${e.label} ${e.count}`);
    reportSummary(SIDEBAR_VALUE.riskFactors, { status: 'ready', count, leads });
  }, [reportSummary, riskFacts]);

  const handleCanRiskDownload = (allOptionsArray, targetId) =>
    downloadCanRisk({
      userId: user_id,
      familyId,
      members: allOptionsArray,
      targetId,
      setSnack
    });

  return (
    <div style={{ maxWidth: 390 }}>
      <Grid container spacing={2}>
        {gender === 'female' && (
          <ButtonGroup size={12}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setGailModalOpen(true)}
              data-va-anchor="pedigree.risk.calculateGail"
              sx={{ width: '100%' }}
            >
              {t('Calculate Gail Score')}
            </Button>
          </ButtonGroup>
        )}

        <ButtonGroup size={12}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCanRiskModalOpen(true)}
            data-va-anchor="pedigree.risk.calculateCanRisk"
            sx={{ width: '100%' }}
          >
            {t('Calculate CanRisk Score')}
          </Button>
        </ButtonGroup>

        {gender === 'female' && (
          <Grid size={12}>
            <RiskRecordsList
              onFacts={handleRiskFacts}
              userId={user_id}
              refreshKey={recordsRefreshKey}
              recordType="gail"
              memberIds={[memberId]}
              scoredOnly
            />
          </Grid>
        )}
        <Grid size={12}>
          <RiskRecordsList
            onFacts={handleRiskFacts}
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="canrisk"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {/* PRS is stored, not calculated in-app (no genotype/VCF data) — a
            score is entered via the "Risk Factor" clinical record (PRS radio
            option) and simply surfaces here as a peer to Gail/CanRisk. */}
        <Grid size={12}>
          <RiskRecordsList
            onFacts={handleRiskFacts}
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="prs"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {/* Not sex-gated: CanRisk covers prostate and pancreatic cancer, and PRS
            is trait-agnostic, so male individuals have risk records too — gating
            this on female left their audit history unreachable. The Gail list
            above stays female-gated because the Gail model itself is. */}
        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <IconButton
            onClick={() =>
              modificationHistoryHandle(CONSTANTS.auditLogType['riskFactors'])
            }
          >
            <HistoryIcon />
          </IconButton>
        </Grid>
      </Grid>
      {/* Calculate Gail Score - fetches saved inputs on open (no form) */}
      <GailScoreModal
        open={gailModalOpen}
        onClose={() => setGailModalOpen(false)}
        data={data}
        familyId={familyId}
        onCalculated={() => setRecordsRefreshKey((k) => k + 1)}
      />
      <CanRiskModal
        data={data}
        open={canRiskModalOpen}
        onClose={(hadResult) => {
          setCanRiskModalOpen(false);
          if (hadResult) setRecordsRefreshKey((k) => k + 1);
        }}
        onDownload={handleCanRiskDownload}
        userId={user_id}
      />
    </div>
  );
}

export default RiskFactorsMenu;
